/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { Checkbox } from "../../../components/ui/checkbox";
import { Label } from "../../../components/ui/label";
import { Button } from "../../../components/ui/button";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useLteX2ConnectionsData } from "./hooks";
import { prepareLteX2connectionsDataGrid, tableContainerSxProps } from "../helpers";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { constStr } from "../../../globals/constants";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { setOpenCustomDailog, setConfirmDialog } from "../../../store/helpers/helperReducers";
import { customDialogSelector, confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { setIsAddNewLteX2Connection, setSelectedLteX2Connection } from "../../../store/api/deviceConfig/reducers";
import AddEditLteX2Connection from "../../../components/Configuration/X2Connection/LteX2Connection/AddEditLteX2Connection";
import { IFullDeviceDetails, IltepeerInterfaces } from "../../../store/api/deviceConfig/types";
import { Pencil, X, Check } from "lucide-react";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { Input } from "../../../components/ui/input";
import { Icon } from "../../../components/Shared/Icon";

interface ILteConnection {
  portVal?: number;
  isEnable?: boolean;
}

const LteX2Connections: React.FC = () => {
  const dispatch = useStoreDispatch();
  const { data, isLoading, permissions, lteX2connectionsColumns, selectedLteX2Connection, deleteLteX2ConnectionHandler, handleUpdate } =
    useLteX2ConnectionsData();

  const getDataFromApi = data?.data[0]?.ltepeerInterfaces || [];
  const isEnableX2Connections = (data?.data[0].enable?.toLowerCase() === "false" ? false : true) || false;
  const localPortX2Connections = data?.data[0].localPort || "";
  const rowsData = prepareLteX2connectionsDataGrid(getDataFromApi);
  const noOfRows = data?.totalItems || 0;
  const [NoOfSelectedRow, setNoOfSelectedRow] = React.useState(0);
  const [modelOpenMode, setModelOpenMode] = React.useState("Add");
  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const isOfflineDevice = selectedDevice?.configurationStatus?.toLowerCase() === "offline";
  const [initialConfig, setInitialConfig] = React.useState<ILteConnection>({ portVal: 0, isEnable: false });

  const openCustomDialog = useSelector(customDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);

  const handleOnAddLteX2Connection = (event: any) => {
    setModelOpenMode("Add");
    dispatch(setIsAddNewLteX2Connection(true));
    dispatch(setOpenCustomDailog(true));
  };

  const handleOnEdit = (event: any) => {
    setModelOpenMode("Edit");
    dispatch(setIsAddNewLteX2Connection(false));
    dispatch(setOpenCustomDailog(true));
    setSelectionModel([]);
    setNoOfSelectedRow(0)
  };

  const handleOnRowSelected = (rowIds: GridRowSelectionModel) => {
    const rowIdLength = rowIds?.length;
    setNoOfSelectedRow(rowIdLength);
    setSelectionModel(rowIds)
    if (rowIdLength === 1) {
      const selectedData = rowsData.filter((item) => item.index === rowIds[0])[0];
      //const selectedRowData = {...initialState.selectedLteX2Connection, ...selectedData};
      dispatch(setSelectedLteX2Connection(selectedData));
    }
  };

  const [isDisable, setIsDisable] = React.useState<any>(true);
  const [lteEditVal, setLteEditVal] = React.useState<ILteConnection>({
    portVal: 0,
    isEnable: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setLteEditVal((prev) => ({
      ...prev,
      [name === "isEnable" ? "isEnable" : "portVal"]: name === "isEnable" ? checked : Number(value),
    }));
  };

  const handleUpdateConfig = () => {
    const updateObj: any = {};
    if (initialConfig.isEnable !== lteEditVal.isEnable) {
      updateObj["Enable"] = lteEditVal.isEnable;
    }
    if (initialConfig.portVal !== lteEditVal.portVal) {
      updateObj["LocalPort"] = lteEditVal.portVal;
    }
    if (initialConfig.isEnable && lteEditVal.isEnable === false) {
      updateObj["Enable"] = false;
    }
    handleUpdate(updateObj, initialConfig.isEnable, initialConfig.portVal);
    setIsDisable(true);
    setInitialConfig(lteEditVal);
  };

  React.useEffect(() => {
    if (!isLoading && data && Array.isArray(data.data) && data.data.length > 0) {
      const enableVal = data.data[0]?.enable?.toLowerCase() !== "false";
      const portVal = Number(data.data[0]?.localPort);

      const fetchedVal = { portVal, isEnable: enableVal };

      setLteEditVal(fetchedVal);
      setInitialConfig(fetchedVal);
    }
  }, [data, isLoading]);

  const handleOnDelete = () => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.LTEX2CONNECTION));
  };

  return (
    <>
      {openCustomDialog && <AddEditLteX2Connection modelOpenMode={modelOpenMode} selectedLteX2Connection={selectedLteX2Connection} />}
      {openConfirmDialog === DIALOGOPENFOR.LTEX2CONNECTION && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={deleteLteX2ConnectionHandler}>
          {`Are you sure you want to delete LTE X2 Connection Peer ${selectedLteX2Connection.index}?`}
        </ConfirmDialog>
      )}
      <div>
        
        <div
          style={{
            minHeight: "300px",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius)",
            padding: "18px",
            boxShadow: "var(--shadow-card)",
            backgroundColor: "var(--card)",
          }}
        >
          <div className="flex flex-row">
            <div className="w-1/2">
              <div className="flex items-center pb-2 h-10">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="isEnable"
                    disabled={isDisable}
                    checked={lteEditVal?.isEnable}
                    onCheckedChange={(checked: boolean) =>
                      setLteEditVal((prev) => ({ ...prev, isEnable: !!checked }))
                    }
                    className="m-0"
                  />
                  <Label htmlFor="isEnable" className="cursor-pointer text-foreground">
                    Enable X2 connections
                  </Label>
                </div>
                <Label htmlFor="portVal" className={`cursor-pointer text-foreground ml-6 ${isDisable ? 'opacity-70' : ''}`}>
                  Local port:
                </Label>

                {isDisable ? (
                  <Label htmlFor="portVals" className="cursor-pointer text-foreground ml-6 opacity-70">
                    {lteEditVal?.portVal}
                  </Label>
                ) : (
                  <div className="w-40" >
                    <Input
                      name="portVal"
                      type="number"
                      value={lteEditVal?.portVal ?? 0}
                      onChange={(e) => setLteEditVal((prev) => ({ ...prev, portVal: Number(e.target.value) }))}
                    />
                  </div>
                )}

                {isDisable && permissions?.hasOpCodeGroup("manage_topology") && !isOfflineDevice && (
                  <Pencil
                    onClick={() => {
                      setIsDisable(!isDisable);
                    }}
                    className="h-4 w-4 ml-10 cursor-pointer text-primary"
                  />
                )}
                {!isDisable && (
                  <div className="ml-5 flex items-center">
                    <X onClick={() => setIsDisable(true)} className="h-4 w-4 ml-1 cursor-pointer text-destructive" />
                    <Check onClick={() => handleUpdateConfig()} className="h-4 w-4 ml-1 cursor-pointer text-green-500" />
                  </div>
                )}

                {permissions?.hasOpCodeGroup("manage_topology") && NoOfSelectedRow === 1 && (
                  <div className="flex gap-2 items-center ml-6">
                    <Button variant="secondary" size="sm" onClick={handleOnEdit}>
                      Edit
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleOnDelete}>
                      <Icon name="delete" size={16} />
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="w-1/2">
              <div className="p-2 w-full flex items-center justify-end">
                {permissions?.hasOpCodeGroup("manage_topology") && !isOfflineDevice && (
                  <Button
                    className="h-10 px-4 text-sm font-medium border border-input bg-background shadow-sm flex items-center gap-2 hover:bg-muted hover:text-accent-foreground transition-colors"
                    variant="outline"
                    onClick={handleOnAddLteX2Connection}
                  >
                    <Icon name="add" size={18} />
                    Add LTE X2 Connection Peer
                  </Button>
                )}
              </div>
            </div>
          </div>
          <hr className="border-t border-border" />
          <div className="mt-3">
            <>
              {NoOfSelectedRow > 0 && (
                <strong className="pl-2">
                  {NoOfSelectedRow} row selected
                </strong>
              )}
              <CustomDataGrid
                tableId="lteX2Connection"
                rows={rowsData}
                columns={lteX2connectionsColumns}
                checkboxSelection={true}
                rowCount={noOfRows}
                loading={isLoading}
                disableColumnMenu={true}
                disableRowSelectionOnClick={true}
                //onRowSelectionModelChange={(ids) => setNoOfSelectedRow(ids?.length)}
                onRowSelectionModelChange={handleOnRowSelected}
                getRowId={(row: IltepeerInterfaces) => row.index!}
                rowSelectionModel={selectionModel}
                isRowSelectable={() => !isOfflineDevice}
                sx={{
                  "& .MuiDataGrid-virtualScroller": { overflowY: "hidden" },
                  "& .MuiDataGrid-scrollbar--vertical": { display: "none" },
                }}
                fillContainer={false}
              />
            </>
          </div>
        </div>
      </div>
    </>
  );
};
export default LteX2Connections;
